'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { NoticeBanner, PickerRow, SettingsCard } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { useCurrencies } from '../hooks/use-currencies';

interface EstimateCurrencySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current display currency (3-letter ISO, uppercase). */
  value: string;
  locale: string;
  /** Apply the new display currency (server converts every amount). */
  onSelect: (currency: string) => void;
}

/**
 * Resolve a localized currency name (e.g. "EUR" → "Euro") for the picker
 * rows. `Intl.DisplayNames` throws on non-ISO codes and unsupported locales —
 * fall back to the bare code so an unknown entry still renders.
 */
function buildCurrencyLabel(
  displayNames: Intl.DisplayNames | null,
  code: string
): string {
  if (displayNames) {
    try {
      const name = displayNames.of(code);
      if (name && name !== code) return `${code} — ${name}`;
    } catch {
      // Non-ISO code — fall through to the bare code.
    }
  }
  return code;
}

/**
 * Display-currency picker as an INDEPENDENT modal dialog (off the estimate
 * title's currency tag) — deliberately NOT one of the estimate settings
 * NestedSheet drill-downs: the settings column holds generation inputs that
 * affect the next regenerate, while the currency switch re-denominates the
 * visible estimate immediately (Apple `EstimateCurrencyPickerSheet` /
 * Android `EstimateCurrencySheet` parity). Lists `CODE — Localized name`
 * rows in the server's order (USD first, then alphabetical — never re-sorted
 * client-side), the active currency checkmarked. Tapping a row applies the
 * currency mutation and closes the dialog.
 */
export function EstimateCurrencySheet({
  open,
  onOpenChange,
  value,
  locale,
  onSelect,
}: EstimateCurrencySheetProps) {
  const t = useTranslations('common.estimate.editor');
  const tEstimate = useTranslations('common.estimate');

  // Defer the catalog fetch until the picker is actually opened — cached
  // for a day after the first open (see useCurrencies).
  const {
    data: currencies,
    isLoading,
    isError,
    refetch,
  } = useCurrencies(open);

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: 'currency' });
    } catch {
      // Unsupported locale — labels degrade to bare codes.
      return null;
    }
  }, [locale]);

  // Always include the CURRENT value so the active currency stays visible
  // (and checkmarked) when the fetched catalog somehow lacks the code.
  const options = useMemo(() => {
    const list = currencies ?? [];
    return list.includes(value) ? list : [value, ...list];
  }, [currencies, value]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col">
        <DialogHeader>
          <DialogTitle>{t('selectCurrency')}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <NoticeBanner
              variant="error"
              message={tEstimate('loadError')}
              action={
                <Button variant="ghost" size="sm" onClick={() => refetch()}>
                  {tEstimate('retry')}
                </Button>
              }
            />
          )}

          {!isLoading && !isError && (
            <SettingsCard>
              {options.map((code) => (
                <PickerRow
                  key={code}
                  title={buildCurrencyLabel(displayNames, code)}
                  isSelected={code === value}
                  onClick={() => {
                    if (code !== value) onSelect(code);
                    onOpenChange(false);
                  }}
                />
              ))}
            </SettingsCard>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
